// src/Sidebar.jsx
import { useEffect, useMemo, useState } from "react";
import FeedbackModal from "./components/FeedbackModal";
import { createPortal } from "react-dom";

const TRELLI_GREEN = "#00B074";
const TRELLI_GOLD  = "#F4C542";

// Simple hook to track mobile breakpoint
function useIsMobile(breakpoint = 768) {
  const mql = useMemo(() => window.matchMedia(`(max-width:${breakpoint}px)`), [breakpoint]);
  const [isMobile, setIsMobile] = useState(mql.matches);

  useEffect(() => {
    const fn = (e) => setIsMobile(e.matches);
    mql.addEventListener?.("change", fn);
    return () => mql.removeEventListener?.("change", fn);
  }, [mql]);

  return isMobile;
}

export default function Sidebar({ onSelect }) {
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Desktop collapse state (persisted)
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem("trelli.sidebarCollapsed") === "1";
  });

  // Mobile drawer state (separate from desktop collapse)
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useIsMobile();

  // Dimensions (desktop)
  const OPEN_WIDTH = 175;
  const TAB_WIDTH  = 22;

  // Keep App shell aligned: on desktop we expose either OPEN_WIDTH or TAB_WIDTH.
  // On mobile, the sidebar is off-canvas → offset is always 0 so content fills the screen.
  useEffect(() => {
    const px = isMobile ? 0 : (collapsed ? TAB_WIDTH : OPEN_WIDTH);
    document.documentElement.style.setProperty("--sidebar-offset", `${px}px`);
  }, [isMobile, collapsed]);

  // Prevent background scroll when mobile drawer is open
  useEffect(() => {
    if (!isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = mobileOpen ? "hidden" : prev || "";
    return () => { document.body.style.overflow = prev; };
  }, [isMobile, mobileOpen]);

  // Close drawer on ESC (mobile)
  useEffect(() => {
    if (!isMobile) return;
    const onKey = (e) => e.key === "Escape" && setMobileOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMobile]);

  // Shared nav list
  const NavList = ({ onPicked }) => (
    <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "1rem" }}>
      {[
        { label: "Dashboard", value: "dashboard" },
        { label: "Budget", value: "budget" },
        { label: "Transactions", value: "transactions" },
        { label: "Reports", value: "reports" },
        { label: "Rules", value: "rules" },
        { label: "Goals", value: "goals" },
        { label: "Settings", value: "settings" },
      ].map(({ label, value }) => (
        <li key={value}>
          <button
            onPointerUp={(e) => {
              if (e.pointerType !== "mouse") {
                e.preventDefault();
                onPicked(value);
              }
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              onPicked(value);
            }}
            onClick={() => onPicked(value)}   // desktop/mouse fallback
            style={{
              background: "none",
              border: "none",
              color: "white",
              fontSize: "0.95rem",
              fontWeight: 400,
              textAlign: "left",
              cursor: "pointer",
              padding: 0,
              WebkitTapHighlightColor: "transparent"
            }}
          >
            {label}
          </button>
        </li>
      ))}

      <li style={{ marginTop: "auto" }}>
        <button
          onClick={() => onPicked("logout")}
          style={{
            background: "none",
            border: "none",
            color: "white",
            fontSize: "0.95rem",
            fontWeight: 400,
            textAlign: "left",
            cursor: "pointer",
            padding: 0
          }}
        >
          Logout
        </button>
      </li>
    </ul>
  );

  // Desktop layout (unchanged visually)
  if (!isMobile) {
    const translateX = collapsed ? -OPEN_WIDTH : 0;
    return (
      <>
        <div
          id="trelli-sidebar"
          style={{
            width: OPEN_WIDTH,
            height: "100vh",
            background: TRELLI_GREEN,
            color: "white",
            padding: "1.5rem 1rem",
            position: "fixed",
            zIndex: 10000,
            top: 0,
            left: 0,
            boxSizing: "border-box",
            overflow: "visible",
            transform: `translateX(${translateX}px)`,
            transition: "transform 240ms ease",
            boxShadow: collapsed ? "none" : "0 0 24px rgba(0,0,0,0.15)",
          }}
        >
          {/* Binder-style tab (desktop only) */}
          <div
            onClick={() => {
              setCollapsed((c) => {
                const next = !c;
                localStorage.setItem("trelli.sidebarCollapsed", next ? "1" : "0");
                return next;
              });
            }}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            style={{
              position: "absolute",
              top: 8,
              right: -TAB_WIDTH + 2,
              width: TAB_WIDTH,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              userSelect: "none",
              background: TRELLI_GREEN,
              color: "#fff",
              borderTopRightRadius: 12,
              borderBottomRightRadius: 12,
              boxShadow: "0 2px 8px rgba(0,0,0,0.20)",
              zIndex: 10001,
              fontSize: 16,
              fontWeight: 800,
              lineHeight: 1,
            }}
          >
            {collapsed ? "»" : "«"}
          </div>

          <h1 style={{ fontSize: "1.8rem", marginBottom: "2rem", fontWeight: "bold" }}>trelli</h1>
          <NavList onPicked={onSelect} />

          {/* Feedback panel */}
          <div
            style={{
              position: "absolute",
              left: 16,
              right: 16,
              bottom: "calc(2rem + 16px)",
              padding: "12px 12px",
              background: "rgba(255,255,255,0.12)",
              backdropFilter: "blur(2px)",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.25)",
            }}
          >
            <div
              style={{
                fontSize: 12,
                lineHeight: 1.2,
                marginBottom: 8,
                color: "white",
                opacity: 0.9,
                textAlign: "center",
              }}
            >
              Spot a bug or have an idea?
            </div>

            <button
              onTouchEnd={(e) => {
                e.preventDefault();
                setMobileOpen(false);
                setFeedbackOpen(true);
              }}
              onPointerUp={(e) => {
                if (e.pointerType !== "mouse") {
                  e.preventDefault();
                  setMobileOpen(false);
                  setFeedbackOpen(true);
                }
              }}
              onClick={() => {
                setMobileOpen(false);
                setFeedbackOpen(true);
              }}
              style={{
                width: "100%",
                fontSize: 12,
                lineHeight: 1.1,
                fontWeight: 700,
                padding: "10px 12px",
                borderRadius: 999,
                border: "none",
                background: TRELLI_GOLD,
                color: "#000",
                cursor: "pointer",
                whiteSpace: "nowrap",
                display: "inline-block",
                WebkitTapHighlightColor: "transparent"
              }}
            >
              Report an issue
            </button>
          </div>

          {feedbackOpen &&
            createPortal(
              <FeedbackModal onClose={() => setFeedbackOpen(false)} />,
              document.body
            )
          }
        </div>
      </>
    );
  }

  // Mobile layout: hamburger + overlay drawer
  return (
    <>
      {/* Hamburger button (top-left). If you want top-right, change left:12 → right:12 and remove left. */}
      <button
        aria-label={mobileOpen ? "Close menu" : "Open menu"}
        onTouchEnd={(e) => { e.preventDefault(); setMobileOpen(v => !v); }}
        onClick={() => setMobileOpen(v => !v)}
        style={{
          position: "fixed",
          top: 12,
          left: 12,           // switch to right: 12 (and remove left) if you prefer top-right
          zIndex: 11000,
          width: 40,
          height: 40,
          borderRadius: 10,
          border: "none",
          background: TRELLI_GREEN,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          lineHeight: 0,
          boxShadow: "0 6px 20px rgba(0,0,0,0.18)",
          cursor: "pointer",
        }}
      >
        {mobileOpen ? (
          // Show an "X" when open so tapping the same button closes it
          <span style={{ fontSize: 18, fontWeight: 900 }}>✕</span>
        ) : (
          // Three bars when closed
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
            <div style={{ width: 20, height: 2, background: "currentColor" }} />
            <div style={{ width: 20, height: 2, background: "currentColor" }} />
            <div style={{ width: 20, height: 2, background: "currentColor" }} />
          </div>
        )}
      </button>

      {/* Drawer + Backdrop */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            onTouchEnd={(e) => { e.preventDefault(); setMobileOpen(false); }}
            onClick={() => setMobileOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.35)",
              zIndex: 10998,
            }}
          />

          {/* Drawer */}
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              height: "100dvh",         // was 100vh — dvh tracks the visible height on mobile
              width: "82vw",
              maxWidth: 320,
              background: TRELLI_GREEN,
              color: "#fff",
              padding: "1.25rem 1rem",
              boxSizing: "border-box",
              zIndex: 10999,
              boxShadow: "0 10px 26px rgba(0,0,0,0.25)",
              display: "flex",
              flexDirection: "column",
              gap: 16,
              overflow: "hidden",       // prevent the drawer itself from extending/scrolling
              animation: "trelliDrawerIn 180ms ease-out",
            }}
          >

            <h1
              style={{
                fontSize: "1.6rem",
                margin: "0 0 0.75rem",
                fontWeight: 800,
                textAlign: "center",
                // add a left margin so the fixed hamburger button doesn't cover the text
                marginLeft: 48,   // equals the 40px button + a little breathing room
                marginRight: 48,
              }}
            >
              trelli
            </h1>

            <div
              style={{
                flex: 1,                      // take all available vertical space
                minHeight: 0,                 // required so overflow works in flex children
                overflowY: "auto",            // only this area scrolls
                WebkitOverflowScrolling: "touch",
                paddingRight: 4,              // tiny gutter so scrollbars don’t cover text
              }}
            >
              <NavList
                onPicked={(val) => {
                  // ensure it closes on the same tap
                  setMobileOpen(false);
                  onSelect(val);
                }}
              />
            </div>

            {/* Feedback button (mobile) */}
            <div
              style={{
                marginTop: "auto",
                padding: 12,
                background: "rgba(255,255,255,0.12)",
                backdropFilter: "blur(2px)",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.25)",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  lineHeight: 1.2,
                  marginBottom: 8,
                  color: "white",
                  opacity: 0.9,
                  textAlign: "center",
                }}
              >
                Spot a bug or have an idea?
              </div>

              <button
                onClick={() => {
                  setMobileOpen(false);
                  setFeedbackOpen(true);
                }}
                style={{
                  width: "100%",
                  fontSize: 12,
                  lineHeight: 1.1,
                  fontWeight: 700,
                  padding: "10px 12px",
                  borderRadius: 999,
                  border: "none",
                  background: TRELLI_GOLD,
                  color: "#000",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  display: "inline-block",
                }}
              >
                Report an issue
              </button>
            </div>
          </div>

          {/* keyframes (inline for portability) */}
          <style>{`
            @keyframes trelliDrawerIn {
              from { transform: translateX(-12px); opacity: 0.8; }
              to   { transform: translateX(0);      opacity: 1; }
            }
          `}</style>
        </>
      )}

      {feedbackOpen &&
        createPortal(
          <FeedbackModal onClose={() => setFeedbackOpen(false)} />,
          document.body
        )
      }
    </>
  );
}